// Loads a device's outputs + last-known status and routes to the correct control
// component — the React equivalent of Flutter's screenRedirector(). Shared by:
//   - DeviceScreen (the /device/:uniqueNumber standalone route)
//   - the desktop shell's right-side device panel (embedded)
//
// Pass a device object (preferred, from a list tile) and/or a uniqueNumber to
// fetch by. `onLoaded(device)` fires once the full record is resolved.

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getDeviceDetail, getOutputs, getDeviceStatus } from '../../api/api';
import { parseStatus } from '../../mqtt/mqttClient';
import { deviceTypeKey } from '../../devices/deviceMeta';
import TouchScreen from './TouchScreen';
import FanScreen from './FanScreen';
import CurtainScreen from './CurtainScreen';
import LightScreen from './LightScreen';
import DoorScreen from './DoorScreen';
import PhaseScreen from './PhaseScreen';
import UnsupportedScreen from './UnsupportedScreen';

export default function DeviceControls({ device: initialDevice, uniqueNumber, onLoaded }) {
  const uno = uniqueNumber || (initialDevice && initialDevice.UniqueNumber);
  const [device, setDevice] = useState(initialDevice || null);
  const [outputs, setOutputs] = useState([]);
  const [initialStatus, setInitialStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        let dev = initialDevice;
        if (!dev && uno) {
          const res = await getDeviceDetail(uno);
          dev = Array.isArray(res.data) ? res.data[0] : res.data;
        }
        const [out, st] = await Promise.all([
          getOutputs(uno),
          getDeviceStatus(uno).catch(() => null),
        ]);
        if (alive) {
          setDevice(dev);
          setOutputs(Array.isArray(out.data) ? out.data : []);
          const row = st && Array.isArray(st.data) ? st.data[0] : null;
          setInitialStatus(row && row.Status ? parseStatus(row.Status) : '');
          if (onLoaded && dev) onLoaded(dev);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Re-run when the target device changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno]);

  if (loading || !device) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const key = deviceTypeKey(device.DeviceID);
  const props = { device, outputs, initialStatus };
  switch (key) {
    case 'oneTouch': return <TouchScreen {...props} gangs={1} />;
    case 'twoTouch': return <TouchScreen {...props} gangs={2} />;
    case 'fourTouch': return <TouchScreen {...props} gangs={4} />;
    case 'fan': return <FanScreen {...props} />;
    case 'curtain': return <CurtainScreen {...props} />;
    case 'rgb': return <LightScreen {...props} channels={['R', 'G', 'B']} />;
    case 'wwh': return <LightScreen {...props} channels={['White', 'Warm']} />;
    case 'wwrgb': return <LightScreen {...props} channels={['W', 'WW', 'R', 'G', 'B']} />;
    case 'door': return <DoorScreen {...props} />;
    case 'singlePhase': return <PhaseScreen {...props} phases={1} />;
    case 'threePhase': return <PhaseScreen {...props} phases={3} />;
    default: return <UnsupportedScreen {...props} />;
  }
}
